import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Clock, Lightbulb, X } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { useT } from '@/store/i18n';
import { api } from '@/lib/api';

const RECENT_KEY = 'baiboon_v8_recent_searches';
const MAX_RECENT = 5;
const DEBOUNCE_MS = 250;
const MIN_QUERY_LEN = 2;

interface Suggestion {
  type: string;
  value: string;
  label: string;
  count?: number;
}

interface AutocompleteResponse {
  suggestions?: Suggestion[];
  data?: {
    suggestions?: Suggestion[];
  };
}

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((v) => typeof v === 'string');
  } catch {}
  return [];
}

function saveRecent(searches: string[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(searches));
  } catch {}
}

function pushRecent(value: string, prev: string[]): string[] {
  const trimmed = value.trim().slice(0, 60);
  if (!trimmed) return prev;
  // dedupe + move-to-front
  const next = [trimmed, ...prev.filter((v) => v !== trimmed)].slice(0, MAX_RECENT);
  saveRecent(next);
  return next;
}

export interface SmartSearchProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}

export function SmartSearch({ value, onChange, placeholder, className }: SmartSearchProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState<string[]>(loadRecent);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Fetch autocomplete suggestions, debounced
  const fetchSuggestions = useCallback((q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < MIN_QUERY_LEN) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api<AutocompleteResponse>(
          `questions/autocomplete.php?q=${encodeURIComponent(q)}`
        );
        // Handle both { suggestions: [...] } and { data: { suggestions: [...] } }
        const items = res.data?.suggestions ?? (res as { suggestions?: Suggestion[] }).suggestions ?? [];
        setSuggestions(items);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  }, []);

  // When value changes externally (e.g. cleared), fetch or clear
  useEffect(() => {
    fetchSuggestions(value);
  }, [value, fetchSuggestions]);

  // Close dropdown on outside click
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  function handleFocus() {
    setOpen(true);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(e.target.value);
    setOpen(true);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    } else if (e.key === 'Enter') {
      if (value.trim()) {
        const next = pushRecent(value, recent);
        setRecent(next);
      }
      setOpen(false);
    }
  }

  function selectSuggestion(val: string) {
    onChange(val);
    const next = pushRecent(val, recent);
    setRecent(next);
    setOpen(false);
  }

  function clearRecent() {
    saveRecent([]);
    setRecent([]);
  }

  const showRecent = open && value.length < MIN_QUERY_LEN && recent.length > 0;
  const showSuggestions = open && value.length >= MIN_QUERY_LEN;
  const showDropdown = showRecent || showSuggestions;

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <Input
        ref={inputRef}
        leftIcon={<Search className="w-4 h-4" />}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-lg py-1 animate-slide-down max-h-72 overflow-y-auto">
          {/* Recent searches */}
          {showRecent && (
            <div>
              <div className="flex items-center justify-between px-3 pt-1.5 pb-1">
                <span className="text-xs font-medium text-slate-400 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  {t('search.recent')}
                </span>
                <button
                  type="button"
                  onClick={clearRecent}
                  className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {t('search.clear_recent')}
                </button>
              </div>
              {recent.map((term) => (
                <button
                  key={term}
                  type="button"
                  onClick={() => selectSuggestion(term)}
                  className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
                >
                  <Clock className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                  <span className="truncate">{term}</span>
                </button>
              ))}
            </div>
          )}

          {/* Autocomplete suggestions */}
          {showSuggestions && (
            <div>
              <div className="flex items-center gap-1.5 px-3 pt-1.5 pb-1">
                <Lightbulb className="w-3 h-3 text-slate-400" />
                <span className="text-xs font-medium text-slate-400">
                  {t('search.suggestions')}
                </span>
                {loading && (
                  <span className="ml-auto text-xs text-slate-300">{t('common.loading')}</span>
                )}
              </div>
              {!loading && suggestions.length === 0 ? (
                <p className="px-3 py-2 text-sm text-slate-400">{t('search.no_suggestions')}</p>
              ) : (
                suggestions.map((s, idx) => (
                  <button
                    key={`${s.type}-${idx}`}
                    type="button"
                    onClick={() => selectSuggestion(s.value)}
                    className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
                  >
                    <span className="truncate flex-1">{s.label}</span>
                    {s.count != null && s.count > 0 && (
                      <span className="flex-shrink-0 text-xs text-slate-400">{s.count}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}

          {/* Clear button when recent is open */}
          {showRecent && (
            <div className="border-t border-slate-100 mt-1 pt-1">
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1.5 transition-colors"
              >
                <X className="w-3 h-3" />
                {t('btn.clear')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
